import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Eye, EyeOff, Pencil, Check, X } from 'lucide-react';
import reportingService from '../services/reportingService';
import config from '../config';
import authService from '../services/authService';

const RANGE_OPTIONS = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 }
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return d.toISOString().split('T')[0];
}

function maskKey(key, revealed) {
  if (!key) return '';
  if (revealed) return key;
  return key.length > 8 ? `${key.slice(0, 4)}…${key.slice(-4)}` : key;
}

export default function KeyDetail() {
  const { apiKeyId } = useParams();
  const navigate = useNavigate();

  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revealed, setRevealed] = useState(false);

  // Inline limit editing
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitDraft, setLimitDraft] = useState('');
  const [savingLimit, setSavingLimit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await reportingService.getKeyDetail(apiKeyId, {
        startDate: isoDaysAgo(days),
        endDate: new Date().toISOString().split('T')[0],
        granularity: 'day'
      });
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load key');
    } finally {
      setLoading(false);
    }
  }, [apiKeyId, days]);

  useEffect(() => {
    load();
  }, [load]);

  const maxUnits = useMemo(() => {
    if (!data?.series?.length) return 0;
    return Math.max(...data.series.map((p) => p.totalUnits));
  }, [data]);

  const saveLimit = async () => {
    setSavingLimit(true);
    setError(null);
    try {
      const trimmed = limitDraft.trim();
      // Empty input clears the cap (null = unlimited); otherwise a positive int.
      let usageLimit = null;
      if (trimmed !== '') {
        const parsed = parseInt(trimmed, 10);
        if (!Number.isFinite(parsed) || parsed <= 0) {
          setError('Usage limit must be a positive whole number, or blank for unlimited.');
          setSavingLimit(false);
          return;
        }
        usageLimit = parsed;
      }
      const response = await fetch(`${config.apiBaseUrl}/api/keys/${apiKeyId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ usageLimit })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to update usage limit');
      }
      setEditingLimit(false);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update usage limit');
    } finally {
      setSavingLimit(false);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(apiKeyId);
    } catch (err) {
      /* no-op */
    }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-200 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-200 p-6">
        <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-200 flex items-center gap-1 mb-4">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="bg-red-950/30 border border-red-800/50 rounded-lg p-6 max-w-lg">
          <p className="text-slate-300 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const key = data?.key;
  const cap = data?.cap;
  const capped = cap && cap.usageLimit !== null && cap.usageLimit !== undefined;
  const pct = capped ? Math.min(100, cap.percentUsed ?? 0) : 0;
  const barColor = !capped
    ? 'bg-indigo-500'
    : cap.exhausted
    ? 'bg-red-500'
    : pct >= 90
    ? 'bg-red-500'
    : pct >= 75
    ? 'bg-amber-500'
    : 'bg-emerald-500';

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200 p-6">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-200 flex items-center gap-1 mb-4 text-sm">
          <ArrowLeft size={16} /> Back
        </button>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-100">
              {key?.nickname || <span className="italic text-slate-500">Unnamed key</span>}
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {key?.product?.name} · {key?.customer?.name} · {key?.plan?.name || 'No plan'}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <span className="font-mono text-xs bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-300">
                {maskKey(apiKeyId, revealed)}
              </span>
              <button onClick={() => setRevealed((v) => !v)} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-200" title={revealed ? 'Hide' : 'Reveal'}>
                {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button onClick={copyKey} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-200" title="Copy key">
                <Copy size={14} />
              </button>
            </div>
          </div>
          <span className={`px-2.5 py-1 text-xs rounded-full ${key?.isEnabled ? 'bg-emerald-900/50 text-emerald-300' : 'bg-red-900/50 text-red-300'}`}>
            {key?.isEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {error && (
          <div className="bg-red-950/30 border border-red-800/50 rounded-lg p-3 mb-4 text-sm text-red-300">{error}</div>
        )}

        {/* Cap card */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Usage limit</h2>
            {!editingLimit && (
              <button
                onClick={() => {
                  setLimitDraft(capped ? String(cap.usageLimit) : '');
                  setEditingLimit(true);
                }}
                className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1 text-xs"
              >
                <Pencil size={13} /> Edit limit
              </button>
            )}
          </div>

          {editingLimit ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="number"
                min="1"
                autoFocus
                placeholder="Blank = unlimited"
                className="p-2 bg-slate-900 rounded border border-slate-600 text-slate-100 focus:outline-none focus:border-slate-500 w-56"
                value={limitDraft}
                onChange={(e) => setLimitDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveLimit();
                  else if (e.key === 'Escape') setEditingLimit(false);
                }}
              />
              <button onClick={saveLimit} disabled={savingLimit} className="px-3 py-2 text-xs bg-emerald-600 rounded hover:bg-emerald-500 text-white flex items-center gap-1 disabled:opacity-50">
                <Check size={13} /> {savingLimit ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setEditingLimit(false)} className="px-3 py-2 text-xs bg-slate-600 rounded hover:bg-slate-500 text-white flex items-center gap-1">
                <X size={13} /> Cancel
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-semibold text-slate-100">
                  {Number(cap?.unitsConsumed || 0).toLocaleString()}
                </span>
                <span className="text-slate-500 text-lg">/ {capped ? Number(cap.usageLimit).toLocaleString() : '∞'}</span>
                <span className="text-sm text-slate-400 ml-2">units consumed</span>
              </div>
              {capped && (
                <>
                  <div className="w-full h-2 bg-slate-700 rounded-full mt-3 overflow-hidden">
                    <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {cap.exhausted ? (
                      <span className="text-red-300 font-medium">Limit reached — this key is inactive.</span>
                    ) : (
                      <>
                        {Number(cap.remaining).toLocaleString()} units remaining ({pct}% used)
                      </>
                    )}
                  </p>
                </>
              )}
              {!capped && (
                <p className="text-xs text-slate-500 mt-2">No limit set — this key can consume without a cap.</p>
              )}
            </>
          )}
        </div>

        {/* Range selector */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-slate-500 uppercase tracking-wider">Usage over</span>
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              onClick={() => setDays(opt.days)}
              className={`px-2.5 py-1 text-xs rounded border ${days === opt.days ? 'bg-slate-700 border-slate-600 text-slate-100' : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Usage chart */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5 mb-6">
          {data?.series?.length ? (
            <div className="flex items-end gap-1 h-32">
              {data.series.map((point) => (
                <div key={point.bucket} className="flex-1 flex flex-col items-center justify-end group relative">
                  <div
                    className="w-full bg-indigo-500/70 hover:bg-indigo-400 rounded-t"
                    style={{ height: `${maxUnits ? Math.max(2, (point.totalUnits / maxUnits) * 100) : 2}%` }}
                    title={`${point.bucket}: ${point.totalUnits.toLocaleString()} units`}
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 italic text-sm text-center py-8">No usage recorded in this window.</p>
          )}
        </div>

        {/* Recent activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
            <div className="bg-slate-800 px-4 py-2.5 border-b border-slate-700">
              <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Recent usage</h3>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {data?.usageEvents?.length ? (
                  data.usageEvents.map((ev) => (
                    <tr key={ev.id} className="border-b border-slate-700/50 last:border-0">
                      <td className="px-4 py-2 text-slate-400">{new Date(ev.created_at).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-slate-200">{Number(ev.units).toLocaleString()} units</td>
                    </tr>
                  ))
                ) : (
                  <tr><td className="px-4 py-4 text-slate-500 italic text-center">No usage events.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
            <div className="bg-slate-800 px-4 py-2.5 border-b border-slate-700">
              <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Recent validations</h3>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {data?.validationEvents?.length ? (
                  data.validationEvents.map((ev) => (
                    <tr key={ev.id} className="border-b border-slate-700/50 last:border-0">
                      <td className="px-4 py-2 text-slate-400">{new Date(ev.validation_timestamp).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">
                        <span className={ev.is_valid ? 'text-emerald-300' : 'text-red-300'}>
                          {ev.is_valid ? 'valid' : 'invalid'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td className="px-4 py-4 text-slate-500 italic text-center">No validations in the last 24h.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
